// src/modules/bookLocation/keyHandover/pages/newSubscription/SubscriptionVerifyCodePage.tsx

import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Loader2 } from "lucide-react";
import type { RootState } from "../../../../../redux/store";
import { useAppDispatch } from "../../../../../redux/useDispatch";
import {
    setNewSubscriptionEmailVerified,
    setNewSubscriptionVerificationCode,
    setNewSubscriptionId,
    setExistingSubscriptionVerified,
    setExistingSubscriptionToken,
    setExistingSubscriptionCustomerAgreementId,
} from "../../../../../redux/slice/subscription/subscriptionSlice";
import { KeyHandoverPageLayout } from "../../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../../components/KeyHandoverContentWrapper";
import { Spinner } from "../../../../../components/ui/spinner";
import { OtpInput } from "../../../../../components/ui/otp-input";
import { Button } from "../../../../../components/ui/button";
import { axiosPrivate } from "../../../../../util/axios";
import routes from "../../../../../routes/routes";
import { useLocationHydration } from "../../hooks/useLocationHydration";
import { useSubscriptionHydration } from "../../hooks/useSubscriptionHydration";
import { useSubscriptionUrlSync } from "../../hooks/useSubscriptionUrlSync";
import { parseSubscriptionQuery } from "../../common/url/subscriptionQuery";
import { isManageIntent } from "../../common/subscriptionManage";
import KolulaStorageManager from "../../../../../storage/KolulaStorageManager";

function useVerifyController() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const hydratedRef = React.useRef(false);

    const locationHydrated = useLocationHydration();

    useSubscriptionHydration();
    useSubscriptionUrlSync(hydratedRef);

    React.useEffect(() => {
        if (!hydratedRef.current) {
            hydratedRef.current = true;
        }
    }, []);

    const subscriptionFlow = useSelector(
        (state: RootState) => state.subscription.hasExistingSubscription
    );
    const isExistingFlow = subscriptionFlow === "existing";
    const newEmail = useSelector(
        (state: RootState) => state.subscription.newSubscription.email
    );
    const existingEmail = useSelector(
        (state: RootState) => state.subscription.existingSubscription.email
    );
    
    // The agreement id is not known yet: `verify/confirm` returns it once the
    // code proves the e-mail address. The e-mail (Redux, else the URL) is what
    // identifies the request.
    const query = React.useMemo(() => parseSubscriptionQuery(location.search), [location.search]);
    const email = (isExistingFlow ? existingEmail : newEmail) || query.email || null;

    const [verificationCode, setVerificationCode] = useState("");
    const [codeError, setCodeError] = useState<string | null>(null);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [resendMessage, setResendMessage] = useState<string | null>(null);
    const [resendError, setResendError] = useState<string | null>(null);

    const handleCodeChange = (nextValue: string) => {
        setVerificationCode(nextValue);
        setCodeError(null);
    };

    const handleVerifyCode = async () => {
        if (verificationCode.length !== 6) {
            setCodeError(
                t("book_location.keys.subscription_verify.error_invalid_length", {
                    defaultValue: "Der Code muss 6 Ziffern lang sein",
                })
            );
            return;
        }

        if (!email) {
            setCodeError(
                t("book_location.keys.subscription_verify.error_missing_email", {
                    defaultValue: "Keine E-Mail-Adresse gefunden. Bitte starte den Prozess neu.",
                })
            );
            return;
        }

        setIsVerifying(true);

        try {
            const payload = {
                email,
                code: verificationCode,
            };

            const response = await axiosPrivate.post<{ customerAgreementId?: string }>(
                "/customer-agreement/verify/confirm",
                payload
            );

            const customerAgreementId = response.data?.customerAgreementId;
            if (!customerAgreementId) {
                throw new Error("customerAgreementId missing in confirm response");
            }

            if (isExistingFlow) {
                dispatch(setExistingSubscriptionCustomerAgreementId(customerAgreementId));
                dispatch(setExistingSubscriptionToken(verificationCode));
                dispatch(setExistingSubscriptionVerified(true));
                if (isManageIntent(location.search)) {
                    // § 312k from any device (plan PR 5): remember the
                    // subscription on this device too, so it also shows under
                    // the bookings. Secondary — a failed write never blocks
                    // the cancellation. The id stays in Redux, not the URL.
                    try {
                        await new KolulaStorageManager().insertSubscriptionId(customerAgreementId);
                    } catch (storageError) {
                        console.warn("[VerifyCode] Could not store the subscription id on this device", storageError);
                    }
                    navigate(routes.keyHandoverSubscriptionManagePath);
                    return;
                }
                const params = new URLSearchParams(location.search);
                params.set("customerAgreementId", customerAgreementId);
                navigate({
                    pathname: routes.keyHandoverServiceSelectionPath,
                    search: `?${params.toString()}`,
                });
            } else {
                dispatch(setNewSubscriptionId(customerAgreementId));
                dispatch(setNewSubscriptionVerificationCode(verificationCode));
                dispatch(setNewSubscriptionEmailVerified(true));
                const params = new URLSearchParams(location.search);
                params.set("customerAgreementId", customerAgreementId);
                // Later steps of the new flow hydrate the id from here.
                params.set("subscriptionId", customerAgreementId);
                navigate({
                    pathname: routes.keyHandoverSubscriptionHostDataPath,
                    search: `?${params.toString()}`,
                });
            }
        } catch (error: any) {
            console.error('[VerifyCode] Error:', error);
            const apiMessage = error?.response?.data?.message;
            setCodeError(
                apiMessage ||
                    t("book_location.keys.subscription_verify.error_verify_failed", {
                        defaultValue: "Fehler bei der Verifizierung. Bitte versuche es erneut.",
                    })
            );
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResend = async () => {
        if (!email) {
            navigate({
                pathname: isExistingFlow
                    ? routes.keyHandoverSubscriptionLoginPath
                    : routes.keyHandoverSubscriptionEmailInputPath,
                search: location.search,
            });
            return;
        }

        setResendMessage(null);
        setResendError(null);
        setIsResending(true);

        try {
            const payload = {
                email,
                flow: isExistingFlow ? "existing" : "new",
            };

            // TODO: replace endpoint with backend-provided path once available
            await axiosPrivate.post(
                "/subscription/verification/resend",
                payload
            );

            setResendMessage(
                t("book_location.keys.subscription_verify.resend_success", {
                    defaultValue: "Neuer Code wurde gesendet.",
                })
            );
        } catch (error: any) {
            setResendError(
                error?.response?.data?.message ||
                    t("book_location.keys.subscription_verify.resend_error", {
                        defaultValue:
                            "Fehler beim Senden des Codes. Bitte versuchen Sie es erneut.",
                    })
            );
        } finally {
            setIsResending(false);
        }
    };

    React.useEffect(() => {
        if (!locationHydrated) return;
        if (email) return;

        const fallbackPath = isExistingFlow
            ? routes.keyHandoverSubscriptionLoginPath
            : routes.keyHandoverSubscriptionEmailInputPath;

        navigate({
            pathname: fallbackPath,
            search: location.search,
        });
    }, [locationHydrated, email, navigate, location.search, isExistingFlow]);

    return {
        email: email ?? null,
        verificationCode,
        isVerifying,
        isResending,
        codeError,
        resendMessage,
        resendError,
        isExistingFlow,
        locationHydrated,
        handleCodeChange,
        handleVerifyCode,
        handleResend,
    };
}

function VerifyCodeContent({
    email,
    verificationCode,
    codeError,
    resendMessage,
    resendError,
    isVerifying,
    isResending,
    onCodeChange,
    onResend,
    locationHydrated,
}: {
    email: string | null;
    verificationCode: string;
    codeError: string | null;
    resendMessage: string | null;
    resendError: string | null;
    isVerifying: boolean;
    isResending: boolean;
    onCodeChange: (nextValue: string) => void;
    onResend: () => void;
    locationHydrated: boolean;
}) {
    const { t } = useTranslation();

    if (!locationHydrated || !email) {
        return (
            <div className="flex justify-center items-center py-12">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6 p-6 max-w-lg mx-auto">
            <div className="text-center space-y-2">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                    <ShieldCheck className="h-8 w-8 text-primary" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900">
                    {t("book_location.keys.subscription_verify.title", {
                        defaultValue: "Code eingeben",
                    })}
                </h2>
                <p className="text-sm text-slate-600">
                    {t("book_location.keys.subscription_verify.description", {
                        defaultValue: "Wir haben einen 6-stelligen Code an {email} gesendet.",
                        email,
                    })}
                </p>
            </div>

            <div className="space-y-2">
                <OtpInput
                    value={verificationCode}
                    onChange={onCodeChange}
                    length={6}
                    disabled={isVerifying || isResending}
                    error={Boolean(codeError)}
                    autoFocus
                    className="justify-center"
                />
                {codeError && (
                    <p className="text-sm text-red-600 mt-1">{codeError}</p>
                )}
            </div>

            <div className="text-center">
                <button
                    onClick={onResend}
                    disabled={isResending}
                    className="inline-flex items-center justify-center text-sm text-primary hover:underline rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {isResending ? (
                        <span className="inline-flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {t("book_location.keys.subscription_verify.resend_sending", {
                                defaultValue: "Sende neuen Code...",
                            })}
                        </span>
                    ) : (
                        t("book_location.keys.subscription_verify.resend_link", {
                            defaultValue: "Code erneut senden",
                        })
                    )}
                </button>
                <div className="mt-2 space-y-1" aria-live="polite">
                    {resendMessage && (
                        <p className="text-sm text-slate-600">{resendMessage}</p>
                    )}
                    {resendError && (
                        <p className="text-sm text-red-600">{resendError}</p>
                    )}
                </div>
            </div>
        </div>
    );
}

const VerifyCodePageInner = () => {
    const { t } = useTranslation();
    const {
        email,
        verificationCode,
        codeError,
        resendMessage,
        resendError,
        isVerifying,
        isResending,
        isExistingFlow,
        locationHydrated,
        handleCodeChange,
        handleVerifyCode,
        handleResend,
    } = useVerifyController();

    const bottomActions = (
        <Button
            size="lg"
            variant="primary"
            className="w-full"
            onClick={handleVerifyCode}
            disabled={isVerifying || verificationCode.length !== 6}
        >
            {isVerifying ? (
                <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("book_location.keys.subscription_verify.verifying", {
                        defaultValue: "Überprüfe Code...",
                    })}
                </>
            ) : (
                t("book_location.keys.subscription_verify.verify_button", {
                    defaultValue: "Code bestätigen",
                })
            )}
        </Button>
    );

    return (
        <KeyHandoverPageLayout
            headerText=""
            backTo={isExistingFlow ? routes.keyHandoverSubscriptionLoginPath : routes.keyHandoverSubscriptionEmailInputPath}
            bottomActions={bottomActions}
        >
            <VerifyCodeContent
                email={email}
                verificationCode={verificationCode}
                codeError={codeError}
                resendMessage={resendMessage}
                resendError={resendError}
                isVerifying={isVerifying}
                isResending={isResending}
                onCodeChange={handleCodeChange}
                onResend={handleResend}
                locationHydrated={locationHydrated}
            />
        </KeyHandoverPageLayout>
    );
};

const SubscriptionVerifyCodePage = () => {
    return (
        <KeyHandoverContentWrapper>
            <VerifyCodePageInner />
        </KeyHandoverContentWrapper>
    );
};

export default SubscriptionVerifyCodePage;
